import React, { memo, useMemo, useState, useRef, useEffect, useLayoutEffect } from 'react'
import classnames from 'classnames'
import moment, { Moment } from 'moment-timezone'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { usePrevious } from 'utils/usePrevious'
import { CalendarBody } from './CalendarBody'
import { CalendarHead } from './CalendarHead'

interface ICalendarViewProps extends JSX.ElementChildrenAttribute {
  startAt: Date
  daysCount: number
  loading?: boolean
  className?: string
  onToggleDriversNames?: () => void
  showDriversNames?: boolean
}

interface IPrevState extends JSX.ElementChildrenAttribute {
  startAt: Date
  days: Moment[]
}

interface ITransition extends IPrevState {
  direction: 'left' | 'right'
}

export const CalendarView = memo<ICalendarViewProps>(
  ({
    children,
    className = '',
    daysCount,
    loading,
    onToggleDriversNames,
    showDriversNames,
    startAt,
  }) => {
    const { isMobile } = useBreakpointContext()
    const days: Moment[] = useMemo(
      () => Array.from({ length: daysCount }, (v, i) => moment(startAt.valueOf()).add(i, 'd')),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [startAt.valueOf(), daysCount]
    )

    const [transition, setTransition] = useState<ITransition>()
    const prev = usePrevious<IPrevState>({ startAt, children, days })
    const isTransitioning = startAt && prev?.startAt && !moment(prev.startAt).isSame(startAt, 'd')

    useLayoutEffect(() => {
      if (isTransitioning && prev) {
        setTransition({
          direction: prev.startAt.valueOf() > startAt.valueOf() ? 'right' : 'left',
          ...prev,
        })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startAt.valueOf()])

    const calendarView = useRef<HTMLDivElement>(null)
    useEffect(() => {
      if (calendarView.current) {
        const onTransitionEnd = () => {
          setTimeout(() => setTransition(undefined), 100)
        }
        calendarView.current.addEventListener('transitionend', onTransitionEnd)
        return () => {
          if (calendarView.current)
            calendarView.current.removeEventListener('transitionend', onTransitionEnd)
        }
      }
      return undefined
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarView.current])

    const calendarViewOverlayClickHandler = () => {
      if (onToggleDriversNames) {
        onToggleDriversNames()
      }
    }

    const currentDays = isTransitioning && prev ? prev.days : days
    const classes = classnames(className, 'calendar-view', {
      'calendar-view-transition-dissolve': transition,
      'calendar-view-show-driver-names': showDriversNames,
    })
    const isLoading = transition ? false : loading
    const isDriverNamesVerticalFill = !isLoading && isMobile

    return (
      <>
        <div className={classes} ref={calendarView}>
          <div
            className='calendar-view-overlay'
            aria-hidden='true'
            onClick={calendarViewOverlayClickHandler}
          />
          <CalendarHead days={transition ? transition.days : currentDays} />
          <CalendarBody days={transition ? transition.days : currentDays} loading={isLoading}>
            {transition ? transition.children : children}
            {isDriverNamesVerticalFill && (
              <div className='calendar-view-driver-names-vertical-fill' />
            )}
          </CalendarBody>
        </div>
        {transition && (
          <div className={`calendar-view calendar-view-transition-${transition.direction}`}>
            <CalendarHead days={days} />
            <CalendarBody days={days} loading>
              {children}
            </CalendarBody>
          </div>
        )}
      </>
    )
  }
)
